/**
 * 公開(docs/20 §2.5・§5): 版の履歴、プレビュー版の作成、公開、取り消し(以前の版に戻す)、
 * 2つの版のファイル比較、プレビュー版をゲームで試すリンク。
 *
 * 操作ボタンは editor 以上にだけ出す(本当の判定は管理用 Functions が毎回行う)。
 * 操作はすべて callable 経由で、Firestore へは書かない。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AdminRole } from '../services/adminAuth';
import { canEdit } from '../services/adminAuth';
import {
  CmsError,
  RELEASE_STATUS_LABELS,
  diffReleaseFiles,
  formatDateTime,
  newOperationId,
  previewGameUrl,
  toCmsError,
  type CmsCallables,
  type CmsReader,
  type ContentMeta,
  type CreatePreviewResult,
  type ReleaseRecord,
} from '../services/cms';

interface Props {
  readonly cms: CmsCallables;
  readonly reader: CmsReader;
  readonly role: AdminRole;
  readonly webAppUrl: string | null;
}

/** 「ゲームで試す」の横の注意(docs/20 §2.4)。 */
export const PREVIEW_ROLE_NOTE =
  'ゲームで Google ログインしているアカウントにも権限(viewer 以上)が必要です';

type Confirm =
  | { readonly kind: 'publish'; readonly releaseId: string; readonly operationId: string }
  | { readonly kind: 'rollback'; readonly releaseId: string; readonly operationId: string };

const CONFIRM_TEXT: Readonly<Record<Confirm['kind'], { title: string; body: string; ok: string }>> =
  {
    publish: {
      title: 'この版を公開しますか?',
      body: '公開すると、ゲームを新しく開いた人からこの版のデータになります。プレイ中の人は開始時の版で記録されます。',
      ok: '公開する',
    },
    rollback: {
      title: 'この版に戻しますか?',
      body: '新しい版は作らず、この版を現在の版に戻します。',
      ok: 'この版に戻す',
    },
  };

function ErrorNotice({ error }: { readonly error: CmsError }) {
  return (
    <div className="notice notice--error" role="alert">
      <div>{error.message}</div>
      {error.errors.length > 0 && (
        <ul className="issue-list" aria-label="検証エラー">
          {error.errors.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      )}
      {error.warnings.length > 0 && (
        <ul className="issue-list issue-list--warn" aria-label="警告">
          {error.warnings.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function countsText(counts: Readonly<Record<string, number>>): string {
  const entries = Object.entries(counts);
  if (entries.length === 0) return '—';
  return entries.map(([key, value]) => `${key} ${value}`).join(' / ');
}

export function PublishPanel({ cms, reader, role, webAppUrl }: Props) {
  const editable = canEdit(role);
  const [releases, setReleases] = useState<readonly ReleaseRecord[] | null>(null);
  const [meta, setMeta] = useState<ContentMeta | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // 「プレビュー版を作る」の1回ぶん。失敗して押し直しても同じ操作IDで送る。
  const [createOperationId, setCreateOperationId] = useState(newOperationId);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatePreviewResult | null>(null);
  const [actionError, setActionError] = useState<CmsError | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [compareFrom, setCompareFrom] = useState('');
  const [compareTo, setCompareTo] = useState('');

  const reload = useCallback(async () => {
    setLoadError(null);
    try {
      const [list, current] = await Promise.all([reader.listReleases(), reader.currentMeta()]);
      setReleases(list);
      setMeta(current);
    } catch (cause) {
      setLoadError(String(cause));
    }
  }, [reader]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // 比較の既定: 現在の版 → 一番新しい版。
  useEffect(() => {
    if (!releases || releases.length === 0) return;
    setCompareTo((value) => value || (releases[0]?.releaseId ?? ''));
    setCompareFrom(
      (value) =>
        value || meta?.releaseId || (releases[1]?.releaseId ?? releases[0]?.releaseId ?? ''),
    );
  }, [releases, meta]);

  const byId = useMemo(
    () => new Map((releases ?? []).map((release) => [release.releaseId, release])),
    [releases],
  );
  const diff = useMemo(() => {
    const from = byId.get(compareFrom);
    const to = byId.get(compareTo);
    if (!from || !to) return null;
    return diffReleaseFiles(from.files, to.files);
  }, [byId, compareFrom, compareTo]);

  async function onCreatePreview(): Promise<void> {
    setBusy(true);
    setActionError(null);
    setCreated(null);
    setDone(null);
    try {
      const result = await cms.createPreview(note.trim(), createOperationId);
      setCreated(result);
      setNote('');
      setCreateOperationId(newOperationId());
      await reload();
    } catch (cause) {
      const error = toCmsError(cause);
      setActionError(error);
      // 検証エラー・権限エラーは同じ操作として再送しても通らない。直したら新しい操作にする。
      if (error.code !== 'network') setCreateOperationId(newOperationId());
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(): Promise<void> {
    if (!confirm) return;
    setBusy(true);
    setActionError(null);
    setDone(null);
    try {
      if (confirm.kind === 'publish') await cms.publish(confirm.releaseId, confirm.operationId);
      else await cms.rollback(confirm.releaseId, confirm.operationId);
      setDone(
        confirm.kind === 'publish'
          ? `${confirm.releaseId} を公開しました。`
          : `${confirm.releaseId} に戻しました。`,
      );
      setConfirm(null);
      await reload();
    } catch (cause) {
      const error = toCmsError(cause);
      setActionError(error);
      // 通信エラーなら同じ操作IDのまま押し直せるようダイアログを残す。
      if (error.code !== 'network') setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pane pane--page">
      <h2 className="section-title">公開</h2>

      <section className="card" aria-label="現在の版">
        <div className="meta-grid">
          <span className="meta-grid__key">現在の版</span>
          <span className="mono">
            {meta ? meta.releaseId : '未公開(同梱の bundled-v1 で動作中)'}
          </span>
          <span className="meta-grid__key">公開日時</span>
          <span>{formatDateTime(meta?.publishedAt ?? null)}</span>
          <span className="meta-grid__key">直前の版</span>
          <span className="mono">
            {meta?.previous
              ? `${meta.previous.releaseId}(${formatDateTime(meta.previous.until)} まで受付)`
              : '—'}
          </span>
        </div>
      </section>

      {editable && (
        <section className="card" aria-label="プレビュー版を作る">
          <h3>プレビュー版を作る</h3>
          <p className="muted">
            いまの下書きを検証し、エラーが無ければ公開前の版(プレビュー版)を作ります。警告があっても作れます。
          </p>
          <label className="field">
            <span className="field__label">メモ(何を変えたか)</span>
            <input
              className="input"
              value={note}
              maxLength={200}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </label>
          <button
            className="button button--primary"
            type="button"
            disabled={busy}
            onClick={() => {
              void onCreatePreview();
            }}
          >
            プレビュー版を作る
          </button>
        </section>
      )}

      {created && (
        <div className="notice notice--ok" role="status">
          <div>
            プレビュー版 <span className="mono">{created.releaseId}</span> を作りました(
            {countsText(created.counts)})。
          </div>
          {created.warnings.length > 0 && (
            <ul className="issue-list issue-list--warn" aria-label="警告">
              {created.warnings.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {done !== null && (
        <div className="notice notice--ok" role="status">
          {done}
        </div>
      )}
      {actionError && <ErrorNotice error={actionError} />}
      {loadError !== null && (
        <div className="notice notice--error" role="alert">
          版の一覧を読み込めませんでした: {loadError}
        </div>
      )}

      <section className="card" aria-label="版の履歴">
        <div className="toolbar">
          <h3>版の履歴</h3>
          <div className="topbar__spacer" />
          <button
            className="button button--small"
            type="button"
            onClick={() => {
              void reload();
            }}
          >
            再読み込み
          </button>
        </div>
        {releases === null ? (
          <p className="muted">読み込み中…</p>
        ) : releases.length === 0 ? (
          <p className="muted">まだ版がありません。</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>版</th>
                <th>状態</th>
                <th>作成</th>
                <th>作成者</th>
                <th>メモ</th>
                <th>件数</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {releases.map((release) => {
                const isCurrent = meta?.releaseId === release.releaseId;
                const isPrevious = meta?.previous?.releaseId === release.releaseId;
                return (
                  <tr key={release.releaseId} data-release={release.releaseId}>
                    <td className="mono">
                      {release.releaseId}
                      {isCurrent && <span className="badge badge--current">現在</span>}
                      {isPrevious && <span className="badge">直前</span>}
                    </td>
                    <td>{RELEASE_STATUS_LABELS[release.status]}</td>
                    <td>{formatDateTime(release.createdAt || null)}</td>
                    <td className="mono">{release.createdBy || '—'}</td>
                    <td>{release.note || '—'}</td>
                    <td className="counts">{countsText(release.counts)}</td>
                    <td className="actions">
                      {webAppUrl !== null && release.status === 'preview' && (
                        <>
                          <a
                            className="button button--small"
                            href={previewGameUrl(webAppUrl, release.releaseId)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            ゲームで試す
                          </a>
                          {/* docs/20 §2.4(2026-10-23 追記): 判定はゲームでログイン中のアカウントの role。 */}
                          <span className="preview-note" data-testid="preview-role-note">
                            {PREVIEW_ROLE_NOTE}
                          </span>
                        </>
                      )}
                      {editable && release.status === 'preview' && (
                        <button
                          className="button button--small button--primary"
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setConfirm({
                              kind: 'publish',
                              releaseId: release.releaseId,
                              operationId: newOperationId(),
                            });
                          }}
                        >
                          公開する
                        </button>
                      )}
                      {editable && release.status !== 'preview' && !isCurrent && (
                        <button
                          className="button button--small"
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setConfirm({
                              kind: 'rollback',
                              releaseId: release.releaseId,
                              operationId: newOperationId(),
                            });
                          }}
                        >
                          この版に戻す
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {webAppUrl === null && (
          <p className="muted">
            VITE_WEB_APP_URL が未設定のため「ゲームで試す」リンクは出していません。
          </p>
        )}
      </section>

      {releases !== null && releases.length > 0 && (
        <section className="card" aria-label="版の比較">
          <h3>版の比較(変わったファイル)</h3>
          <div className="toolbar">
            <select
              className="select select--inline"
              aria-label="比較元"
              value={compareFrom}
              onChange={(event) => {
                setCompareFrom(event.target.value);
              }}
            >
              {releases.map((release) => (
                <option key={release.releaseId} value={release.releaseId}>
                  {release.releaseId}
                </option>
              ))}
            </select>
            <span>→</span>
            <select
              className="select select--inline"
              aria-label="比較先"
              value={compareTo}
              onChange={(event) => {
                setCompareTo(event.target.value);
              }}
            >
              {releases.map((release) => (
                <option key={release.releaseId} value={release.releaseId}>
                  {release.releaseId}
                </option>
              ))}
            </select>
          </div>
          {diff && (
            <div aria-label="比較結果">
              <p className="muted">
                追加 {diff.added.length} / 削除 {diff.removed.length} / 変更 {diff.changed.length} /
                同じ {diff.unchanged}
              </p>
              <ul className="diff-list">
                {diff.added.map((path) => (
                  <li key={`a-${path}`} className="diff-list__added">
                    <span className="mono">+ {path}</span>
                  </li>
                ))}
                {diff.removed.map((path) => (
                  <li key={`r-${path}`} className="diff-list__removed">
                    <span className="mono">- {path}</span>
                  </li>
                ))}
                {diff.changed.map((path) => (
                  <li key={`c-${path}`} className="diff-list__changed">
                    <span className="mono">~ {path}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {confirm && (
        <div className="dialog-backdrop" role="presentation">
          <div
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-label={CONFIRM_TEXT[confirm.kind].title}
          >
            <h2 className="dialog__title">{CONFIRM_TEXT[confirm.kind].title}</h2>
            <p className="dialog__target mono">{confirm.releaseId}</p>
            <p>{CONFIRM_TEXT[confirm.kind].body}</p>
            <div className="dialog__actions">
              <button
                className="button"
                type="button"
                disabled={busy}
                onClick={() => {
                  setConfirm(null);
                }}
              >
                やめる
              </button>
              <button
                className="button button--primary"
                type="button"
                disabled={busy}
                onClick={() => {
                  void onConfirm();
                }}
              >
                {CONFIRM_TEXT[confirm.kind].ok}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
